/**
 * assets/theme.js
 * Women's Fashion - Shopify Theme
 * Converted from React/Framer Motion source
 */

/* ============================================================
   1. HEADER — sticky shadow + mobile menu + search overlay
   ============================================================ */
/* Header: announcement · nav pill · search · mobile overlay · transitions
   are all handled by assets/header.js (loaded separately). */


/* ============================================================
   2. SCROLL-BASED ANIMATIONS (IntersectionObserver)
   ============================================================ */
(function initScrollAnimations() {
  const targets = document.querySelectorAll('[data-animate]');
  if (!targets.length) return;

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -40px 0px'
  });

  targets.forEach(el => io.observe(el));
})();

/* ============================================================
   3. MARQUEE SPEED via CSS variable
   ============================================================ */
(function initMarquee() {
  const marqueeSection = document.querySelector('.marquee-section');
  if (!marqueeSection) return;

  // Speed is set by section schema — read it from a data attribute if present
  const speed = marqueeSection.dataset.speed || 30;
  const inner = marqueeSection.querySelector('.marquee-inner');
  if (inner) inner.style.animationDuration = `${speed}s`;
})();

/* ============================================================
   4. PRODUCT GALLERY — thumbnail switcher
   ============================================================ */
(function initProductGallery() {
  const thumbs = document.querySelectorAll('.product-gallery-thumb');
  const mainImg = document.querySelector('.product-gallery-main img');
  if (!thumbs.length || !mainImg) return;

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src    = thumb.querySelector('img')?.src;
      const srcset = thumb.querySelector('img')?.srcset;
      if (src) mainImg.src = src;
      if (srcset) mainImg.srcset = srcset;
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
})();

/* ============================================================
   5. QUANTITY INPUT — increment / decrement
   ============================================================ */
(function initQuantityInputs() {
  document.querySelectorAll('.quantity-wrap').forEach(wrap => {
    const input   = wrap.querySelector('.qty-input');
    const minusBtn = wrap.querySelector('[data-qty="minus"]');
    const plusBtn  = wrap.querySelector('[data-qty="plus"]');
    if (!input) return;

    const update = delta => {
      const val = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 999;
      input.value = Math.min(max, Math.max(min, val + delta));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    if (minusBtn) minusBtn.addEventListener('click', () => update(-1));
    if (plusBtn)  plusBtn.addEventListener('click',  () => update(1));
  });
})();

/* ============================================================
   6. CART — AJAX add-to-cart (Shopify)
   ============================================================ */
(function initAjaxCart() {
  const addToCartForms = document.querySelectorAll('[data-add-to-cart-form]');
  if (!addToCartForms.length) return;

  addToCartForms.forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();

      const btn = form.querySelector('[data-add-to-cart]');
      const originalText = btn ? btn.textContent : '';
      if (btn) {
        btn.textContent = 'Adding...';
        btn.disabled = true;
      }

      const formData = new FormData(form);

      try {
        const res = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        if (!res.ok) throw new Error('Cart error');

        await res.json();
        updateCartCount();

        if (btn) {
          btn.textContent = 'Added!';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        }

        // Show success notification
        showNotification('Item added to cart!', 'success');
      } catch (err) {
        console.error(err);
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
        showNotification('Error adding item. Please try again.', 'error');
      }
    });
  });

  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      const count = data.item_count || 0;
      document.querySelectorAll('.cart-count').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    } catch (_) {}
  }
})();

/* ============================================================
   7. TOAST NOTIFICATIONS
   ============================================================ */
function showNotification(message, type = 'success') {
  const existing = document.getElementById('theme-notification');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = 'theme-notification';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  const bg = type === 'success'
    ? 'var(--color-primary)'
    : '#c0392b';

  Object.assign(el.style, {
    position:     'fixed',
    bottom:       '1.5rem',
    right:        '1.5rem',
    background:   bg,
    color:        '#fff',
    padding:      '1rem 1.5rem',
    borderRadius: 'var(--radius-lg)',
    fontFamily:   'var(--font-body)',
    fontSize:     '0.95rem',
    fontWeight:   '600',
    boxShadow:    'var(--shadow-xl)',
    zIndex:       '9999',
    transform:    'translateY(100px)',
    opacity:      '0',
    transition:   'all 0.4s cubic-bezier(.16,1,.3,1)',
    maxWidth:     '300px'
  });

  el.textContent = message;
  document.body.appendChild(el);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.style.transform = 'translateY(0)';
      el.style.opacity   = '1';
    });
  });

  setTimeout(() => {
    el.style.transform = 'translateY(100px)';
    el.style.opacity   = '0';
    setTimeout(() => el.remove(), 400);
  }, 3500);
}

/* ============================================================
   8. HERO PARALLAX (lightweight CSS-driven fallback)
   ============================================================ */
(function initParallax() {
  const heroImg = document.querySelector('.hero-image-inner');
  if (!heroImg || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        const scrollY   = window.scrollY;
        const heroH     = heroImg.closest('.hero-section')?.offsetHeight || window.innerHeight;
        const progress  = Math.min(scrollY / heroH, 1);
        const translate = progress * 30; // max 30% shift
        const scaleVal  = 1 + progress * 0.1;
        heroImg.style.transform  = `translateY(${translate}%) scale(${scaleVal})`;
        heroImg.style.opacity    = (1 - progress * 1.25).toString();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
})();

/* ============================================================
   9. COLLECTION CARDS — scroll reveal
   ============================================================ */
(function initCollectionCards() {
  const cards = document.querySelectorAll('.collection-card');
  if (!cards.length) return;

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      entry.target.style.opacity    = entry.isIntersecting ? '1' : '0';
      entry.target.style.transform  = entry.isIntersecting ? 'translateY(0)' : 'translateY(40px)';
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px'
  });

  cards.forEach((card, i) => {
    card.style.transition = `opacity .7s ${i * 0.07}s cubic-bezier(.16,1,.3,1), transform .7s ${i * 0.07}s cubic-bezier(.16,1,.3,1)`;
    card.style.opacity    = '0';
    card.style.transform  = 'translateY(40px)';
    io.observe(card);
  });
})();

/* ============================================================
   10. VISUAL BREAK parallax background
   ============================================================ */
(function initVisualBreakParallax() {
  const section = document.querySelector('.visual-break');
  const bg      = section && section.querySelector('.visual-break-img');
  if (!bg || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      let ticking = false;
      const handler = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const rect     = section.getBoundingClientRect();
            const vh       = window.innerHeight;
            const progress = (vh - rect.top) / (vh + rect.height);
            const shift    = (progress - 0.5) * 40;
            bg.style.transform = `translateY(${shift}px)`;
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener('scroll', handler, { passive: true });
      io.unobserve(entry.target);
    });
  }, { threshold: 0.01 });

  io.observe(section);
})();

/* ============================================================
   11. CONTACT FORM feedback (Shopify handles submit natively)
   ============================================================ */
(function initContactForm() {
  const successMsg = document.querySelector('.contact-success-msg');
  if (successMsg) {
    successMsg.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
})();

/* ============================================================
   12. VARIANT SELECTOR — update price on selection
   ============================================================ */
(function initVariantSelector() {
  const variantSelects = document.querySelectorAll('.variant-select[data-variants]');
  variantSelects.forEach(select => {
    const priceEl = document.querySelector('.product-info-price');
    const variants = JSON.parse(select.dataset.variants || '[]');

    select.addEventListener('change', () => {
      const chosen = variants.find(v => v.id == select.value);
      if (chosen && priceEl) {
        const formatted = (chosen.price / 100).toLocaleString('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });
        priceEl.textContent = formatted;
      }
    });
  });
})();
